import { useState } from "react";
import { ERROR_MESSAGES } from "../../constant/constant.js";

/**
 * @typedef {Object} Kid
 * @property {number} [id] - Required in case of updating the user details
 * @property {string} firstName - First name of the kid
 * @property {string} lastName - Last name of the kid
 * @property {string} username - Username of the kid
 * @property {string} password - Password of the kid
 * @property {string} [email] - Optional email of the kid
 * @property {number} age - Age of the kid
 * @property {boolean} transfer_progress - Whether to transfer progress
 */

/**
 * @typedef {Object} OnboardingData
 * @property {Kid[]} kids - Array of kids to add during onboarding
 */

/**
 * @typedef {Object} OnboardingStudent
 * @property {number} id
 * @property {number|null} tenant_id
 * @property {number} inst_id
 * @property {string} name
 * @property {string} username
 * @property {number} is_pswd_reset
 * @property {string|null} email
 * @property {string} locale
 * @property {string} age
 * @property {number} role_id
 * @property {number|null} assigned_to
 * @property {string} createdBy
 * @property {number|null} class_id
 * @property {string} status
 * @property {string} created_at
 * @property {string} updated_at
 * @property {string} student_code
 */

/**
 * @typedef {Object} OnboardingResponse
 * @property {string} message
 * @property {OnboardingStudent[]} students
 */

/**
 * @typedef {Object.<string, string[]>} FieldErrors
 * Keyed by field path (e.g. "kids.1.username"), value is array of error messages.
 */

/**
 * Custom hook to complete user onboarding
 */
export const useCompleteOnboarding = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  /** @type {[OnboardingResponse|null, Function]} */
  const [data, setData] = useState(null);
  /** @type {[FieldErrors|null, Function]} */
  const [fieldErrors, setFieldErrors] = useState(null);

  const apiUrl = process.env.REACT_APP_API_URL;

  /**
   * Completes the user onboarding process
   * @param {OnboardingData} onboardingData - The user onboarding data
   * @returns {Promise<OnboardingResponse|null>} The response data on success, null on failure
   */
  const completeOnboarding = async (onboardingData) => {
    setLoading(true);
    setError("");
    setData(null);
    setFieldErrors(null);

    try {
      const parentLocalData = JSON.parse(
        localStorage.getItem("parentLocalData"),
      );
      const parentToken = parentLocalData?.token;

      if (!parentToken) {
        throw new Error(ERROR_MESSAGES.FORBIDDEN);
      }

      const response = await fetch(`${apiUrl}/auth/complete-onboarding`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${parentToken}`,
        },
        body: JSON.stringify(onboardingData),
      });

      if (!response.ok) {
        let errorMsg = ERROR_MESSAGES?.UNKNOWN || "Something went wrong";
        try {
          const errorData = await response.json();
          if (errorData?.errors) {
            // Store the full errors object for field-level display
            setFieldErrors(errorData.errors);
            // Also set a generic message
            errorMsg = errorData?.message || "Validation failed";
          } else {
            errorMsg = errorData?.message || errorMsg;
          }
        } catch {
          if (response.status === 500) {
            errorMsg =
              ERROR_MESSAGES?.INTERNAL_SERVER || "Internal server error";
          }
        }
        throw new Error(errorMsg);
      }

      /** @type {OnboardingResponse} */
      const responseData = await response.json();
      setData(responseData);
      return responseData;
    } catch (err) {
      setError(
        err?.message || ERROR_MESSAGES?.UNKNOWN || "Something went wrong",
      );
      return null;
    } finally {
      setLoading(false);
    }
  };

  return {
    completeOnboarding,
    loading,
    error,
    fieldErrors,
    data,
    setError,
    setFieldErrors,
  };
};
